import React, { useState, useEffect, useRef } from 'react';
import { authService } from '../../services/authService';
import { ROLE_STYLES, QUEUE_CLASS_LABELS, QUEUE_CLASS_ICONS, basePriority } from '../../lib/roles';
import { ENDPOINTS } from '../../config/endpoints';

// v2 транскрибации и v1 АТС выведены из эксплуатации — их домены теперь
// редиректят на v3, поэтому опрос приводил к дублям bit-transcribe-v3 /
// bit-dual-channel-v3 в очереди. Оставляем только действующие сервисы.
// Без .filter(s => s.url): каждый ключ ENDPOINTS теперь всегда непустой —
// либо адрес из переменной, либо запасной same-origin путь (config/endpoints.js),
// так что предохранитель на пустой url был мёртвым кодом.
const SERVICES = [
  { url: ENDPOINTS.asr, label: 'Транскрибация v3' },
  { url: ENDPOINTS.diarize, label: 'Диаризация v3' },
  { url: ENDPOINTS.dual, label: 'АТС (стерео) v3' },
  { url: ENDPOINTS.tts, label: 'Синтез речи (TTS)' },
];

const POLL_INTERVAL = 5000;

function formatWait(sec) {
  if (sec < 60) return `${sec}с`;
  return `${Math.floor(sec / 60)}м ${sec % 60}с`;
}

function formatDuration(sec) {
  if (!sec) return '—';
  if (sec < 60) return `${Math.round(sec)}с`;
  return `${Math.floor(sec / 60)}м ${Math.round(sec % 60)}с`;
}

// Раскладка скора по слагаемым для тултипа. Бэкенд отдаёт только итоговый
// priority_score — роль и класс раскладываем сами через basePriority() (те же
// числа, что и в карточке пользователя), а всё остальное (квота, дневная
// нагрузка, длительность, новый пользователь, ожидание) сворачиваем в один
// остаток — по отдельности эти слагаемые в ответе /admin/queue не приходят.
function scoreBreakdownTitle(role, queueClass, score) {
  if (score == null) return undefined;
  const roleBase = basePriority(role, 'normal');
  const withClass = basePriority(role, queueClass || 'normal');
  const classDelta = withClass - roleBase;
  const rest = score - withClass;
  const classSign = classDelta >= 0 ? '+' : '';
  return `роль ${roleBase} · класс ${classSign}${classDelta} · остальное ${rest.toFixed(1)}`;
}

function ServiceQueue({ label, data, loading, error }) {
  const tasks = data?.tasks || [];

  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <h3 className="font-medium text-paper-ink-2">{label || data?.service || '—'}</h3>
        {data?.service && data.service !== label && (
          <span className="text-xs text-paper-hint font-mono">{data.service}</span>
        )}
        {data && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            data.active > 0 ? 'bg-paper-panel text-paper-accent' : 'bg-paper-bg text-paper-ink-3'
          }`}>
            {data.active} активных
          </span>
        )}
        {/* Слот пакетного класса — сервис должен отдать оба поля разом (Task 5
            для bit-transcribe-v3), у остальных сервисов их пока нет. */}
        {data && data.batch_running != null && data.batch_max_slots != null && (
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium bg-paper-bg text-paper-ink-3"
            title="Занятость пакетного слота"
          >
            {QUEUE_CLASS_ICONS.batch} {data.batch_running}/{data.batch_max_slots}
          </span>
        )}
      </div>
      {loading ? (
        <div className="text-sm text-paper-hint py-2">Загрузка...</div>
      ) : error ? (
        <div className="text-sm text-paper-accent py-2">{error}</div>
      ) : tasks.length === 0 ? (
        <div className="text-sm text-paper-hint">Очередь пуста</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-paper-hint border-b border-paper-line">
                <th className="pb-1 pr-4 font-medium">task_id</th>
                <th className="pb-1 pr-4 font-medium">email</th>
                <th className="pb-1 pr-4 font-medium">role</th>
                <th className="pb-1 pr-4 font-medium">класс</th>
                <th className="pb-1 pr-4 font-medium">score</th>
                <th className="pb-1 pr-4 font-medium">статус</th>
                <th className="pb-1 pr-4 font-medium">прогресс</th>
                <th className="pb-1 pr-4 font-medium">ждёт</th>
                <th className="pb-1 font-medium">длина</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.task_id} className="border-b border-paper-line hover:bg-paper-panel">
                  <td className="py-1.5 pr-4 font-mono text-xs text-paper-ink-3">{t.task_id.slice(0, 8)}…</td>
                  <td className="py-1.5 pr-4 text-paper-ink-2">{t.email || '—'}</td>
                  <td className="py-1.5 pr-4">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${ROLE_STYLES[t.role] || ROLE_STYLES.user}`}>
                      {t.role || '—'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-4">
                    <span title={QUEUE_CLASS_LABELS[t.queue_class] || t.queue_class}>
                      {QUEUE_CLASS_ICONS[t.queue_class] || t.queue_class || '—'}
                    </span>
                  </td>
                  <td
                    className="py-1.5 pr-4 font-mono text-xs"
                    title={scoreBreakdownTitle(t.role, t.queue_class, t.priority_score)}
                  >
                    {t.priority_score != null ? t.priority_score.toFixed(1) : '—'}
                  </td>
                  <td className="py-1.5 pr-4">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      t.status === 'PROCESSING'
                        ? 'bg-paper-bg text-paper-ink-2'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="py-1.5 pr-4 text-paper-ink-2">{t.progress != null ? `${t.progress}%` : '—'}</td>
                  <td className="py-1.5 pr-4 text-paper-ink-3">{formatWait(t.waiting_sec)}</td>
                  <td className="py-1.5 text-paper-ink-3">{formatDuration(t.audio_duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function QueueTab() {
  const [queues, setQueues] = useState(SERVICES.map(() => ({ data: null, loading: true, error: null })));
  const intervalRef = useRef(null);

  async function fetchAll() {
    const headers = authService.getAuthHeaders();

    const results = await Promise.all(
      SERVICES.map(({ url }) =>
        fetch(`${url}/admin/queue`, { headers })
          .then(r => r.ok ? r.json() : Promise.reject(`Ошибка ${r.status}`))
          .then(d => ({ data: d, loading: false, error: null }))
          .catch(e => ({ data: null, loading: false, error: String(e) }))
      )
    );
    setQueues(results);
  }

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []);

  const totalActive = queues.reduce((sum, q) => sum + (q.data?.active || 0), 0);

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-paper-ink-3">Обновляется каждые 5 секунд</span>
          {totalActive > 0 && (
            <span className="text-xs bg-paper-accent text-white px-2 py-0.5 rounded-full">
              {totalActive} в очереди
            </span>
          )}
        </div>
        <button
          className="text-xs text-paper-accent hover:underline"
          onClick={() => { setQueues(prev => prev.map(q => ({ ...q, loading: true }))); fetchAll(); }}
        >
          Обновить
        </button>
      </div>

      {queues.map((q, i) => (
        <ServiceQueue key={SERVICES[i].url} label={SERVICES[i].label} data={q.data} loading={q.loading} error={q.error} />
      ))}
    </div>
  );
}
